/**
 * Per-currency BAND CONTEXT for the PL × currency matrix (P3, 2026-08-31) —
 * pure, zero I/O.
 *
 * THE QUESTION THIS ANSWERS. The currency matrix renders manually-locked
 * prices (`currencyPriceRows`), which say nothing about which band set the
 * product is bucketed on. Two products in different product groups therefore
 * rendered IDENTICAL matrix chrome even after P1 gave them different band
 * sets — the per-group scoping was invisible on the surface that is supposed
 * to show a product's currency picture. This module supplies the missing
 * half: for each currency COLUMN, the band the product actually resolves into
 * IN THAT CURRENCY, and the bounds of that band.
 *
 * THE THREE FENCES IT KEEPS.
 * - RULING R-BAND: the bucketing quantity is `breakdown.pricingBase`, for
 *   every product, whatever its anchor. This module calls `resolvePriceGroup`
 *   with exactly that operand — it neither re-implements the lookup nor moves
 *   it onto the anchor's figure.
 * - D27 / the manual-lock doctrine: a currency the product has NO cost basis
 *   in yields NO band. There is no FX conversion here and no signature that
 *   could carry a rate — a missing basis is reported as the explicit
 *   `no_cost_basis_in_currency` state, which the surface renders as the dash
 *   placeholder, never as a converted or computed number.
 * - P1's scope: WHICH set is eligible comes from `effectiveBandGroupOf`, the
 *   same call the engine and the product-page source cards make. A one-sided
 *   omission here would print one product group's bounds under another's
 *   label.
 *
 * WHY A SEPARATE MODULE and not `currencyMatrix.ts`: that file is imported BY
 * `resolvePrice.ts` (for `selectCurrencyPriceRow`), so reaching back into the
 * resolver from it would close an import cycle. This module sits ABOVE both.
 */

import type { PriceGroupBand, PriceSource, PricingDataset } from '@/types/api/pricing';
import {
  buildCostBreakdown,
  effectiveBandGroupOf,
  resolvePriceGroup,
  resolvedBandSetOwner,
  selectCostRecord,
} from './resolvePrice';

/**
 * One distinct band outcome inside a currency, and the sources that share it.
 *
 * A product can hold cost records from BOTH sources in the same currency
 * (seeded: `prod-headlight-hal-01` carries CH and TW records, both AUD), and
 * their pricing bases differ — so they can legitimately land in different
 * bands. Collapsing that to one figure would be a lie in exactly the place
 * this module exists to stop one, so distinct outcomes stay distinct and the
 * surface names the source when there is more than one.
 */
export interface CurrencyBandFacet {
  /** Sources whose pricing base lands on this same outcome, in seed order. */
  sources: PriceSource[];
  /** The matched band, or `null` when no active band covers the base. */
  band: PriceGroupBand | null;
  /** The quantity the band was looked up on — `breakdown.pricingBase`. */
  pricingBase: number;
}

export interface CurrencyBandContext {
  currency: string;
  /**
   * The product's owning product group, from its classification. NOT the same
   * question as which set it is banded on — a group that owns no cuts is
   * banded on the default set. Carried for traceability; the LABEL must read
   * `bandSetOwnerId`.
   */
  productGroupId: string | null;
  /**
   * WHOSE band set the bounds above actually came from: the product group that
   * owns the eligible set, or `null` for the DEFAULT (A–D) set.
   *
   * Separate from `productGroupId` because the first draft of this surface
   * conflated them and captioned "Band set: Brake Pads" over the DEFAULT set's
   * bounds — a confident wrong answer about which numbers the reader is
   * looking at. Resolved through the engine's own `resolvedBandSetOwner`, so
   * the caption and the selection cannot drift.
   */
  bandSetOwnerId: string | null;
  /**
   * Distinct band outcomes in this currency. EMPTY = the product has no cost
   * basis in this currency at all, which is the dash state: no band, and
   * emphatically no converted figure.
   */
  facets: CurrencyBandFacet[];
}

/**
 * Resolve one currency column's band context for one product.
 *
 * Only cost records whose OWN currency is `currency` are considered — the
 * breakdown carries the record's currency, so this is an exact match and
 * never a conversion.
 */
export function resolveCurrencyBandContext(
  dataset: PricingDataset,
  productId: string,
  currency: string,
  asOf: string
): CurrencyBandContext {
  const productGroupId = effectiveBandGroupOf(dataset, productId);
  const bandSetOwnerId = resolvedBandSetOwner(dataset.priceGroupBands, productGroupId);
  const facets: CurrencyBandFacet[] = [];

  /**
   * The sources this PRODUCT actually holds a record in, for THIS currency —
   * derived from the data rather than from a hardcoded `['CH','TW']` list.
   * A second copy of the source vocabulary in the pure library is exactly the
   * kind of duplicate that drifts, and `PRICE_SOURCES` lives in the UI hook
   * layer, which this module must not depend on.
   */
  const sources = Array.from(
    new Set(
      dataset.costRecords
        .filter((record) => record.productId === productId && record.currency === currency)
        .map((record) => record.source)
    )
  );

  for (const source of sources) {
    const record = selectCostRecord(dataset.costRecords, productId, asOf, source);
    if (!record || record.currency !== currency) continue;
    const breakdown = buildCostBreakdown(record);
    const band = resolvePriceGroup(
      dataset.priceGroupBands,
      breakdown.pricingBase,
      breakdown.currency,
      productGroupId
    );
    const existing = facets.find(
      (facet) =>
        (facet.band?.id ?? null) === (band?.id ?? null) &&
        facet.pricingBase === breakdown.pricingBase
    );
    if (existing) {
      existing.sources.push(source);
    } else {
      facets.push({ sources: [source], band, pricingBase: breakdown.pricingBase });
    }
  }

  return { currency, productGroupId, bandSetOwnerId, facets };
}

/** Every currency column's context, in the column order the caller passes. */
export function buildCurrencyBandContexts(
  dataset: PricingDataset,
  productId: string,
  currencies: readonly string[],
  asOf: string
): CurrencyBandContext[] {
  return currencies.map((currency) =>
    resolveCurrencyBandContext(dataset, productId, currency, asOf)
  );
}
